import type { ReactNode } from "react";

import {
  MarketingSection,
  SectionIntro,
  SectionKicker,
  SectionTitle,
} from "@/components/marketing/MarketingSection";
import LineIcon from "@/components/marketing/sections/LineIcon";
import { ICON_TONES, STEM_ICONS } from "@/components/marketing/stems/stemIcons";
import { cn } from "@/lib/utils";

const STEMS = [
  { label: "Vocals", icon: STEM_ICONS.vocals, tone: ICON_TONES.blue },
  { label: "Drums", icon: STEM_ICONS.drums, tone: ICON_TONES.pink },
  { label: "Bass", icon: STEM_ICONS.bass, tone: ICON_TONES.teal },
  { label: "Guitar", icon: STEM_ICONS.guitar, tone: ICON_TONES.orange },
  { label: "Piano", icon: STEM_ICONS.piano, tone: ICON_TONES.blue },
  { label: "Other", icon: STEM_ICONS.other, tone: ICON_TONES.blue },
];

function StemResult({
  label,
  icon,
  tone,
  className,
}: {
  label: string;
  icon: ReactNode;
  tone: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-[51px] items-center gap-3 rounded-[14px] border border-[#DDE2EE] bg-white px-[13px] py-[10px]",
        className,
      )}
    >
      <i
        className={cn(
          "grid size-[31px] shrink-0 place-items-center rounded-[9px]",
          tone,
        )}
      >
        <LineIcon className="size-[19px]">{icon}</LineIcon>
      </i>
      <span className="font-figtree text-[14px] font-extrabold leading-[1.2]">
        {label}
      </span>
    </div>
  );
}

function Connector() {
  return <span aria-hidden className="h-[18px] w-px bg-[#BFC7D9]" />;
}

export default function StemsCatalog() {
  return (
    <MarketingSection
      className="bg-white !py-[88px] max-[680px]:!py-[76px]"
      innerClassName="grid grid-cols-[minmax(0,0.92fr)_minmax(420px,1.08fr)] items-center gap-[clamp(45px,7vw,90px)] max-[980px]:grid-cols-1 max-[980px]:gap-[34px]"
    >
      <div>
        <SectionKicker>Back catalog</SectionKicker>
        <SectionTitle className="!max-w-[620px]">
          Your back catalog has more to give.
        </SectionTitle>
        <SectionIntro className="!mt-6 !max-w-[600px] !leading-[1.68]">
          Bring older recordings into your current workflow. Lutely separates
          vocals and instruments, even when the original session files are long
          gone, so more of your catalog is ready for performances,
          collaborations, and licensing requests.
        </SectionIntro>
      </div>
      <div
        role="img"
        aria-label="One finished recording separated into an instrumental and six organized stems: vocals, drums, bass, guitar, piano, and other"
        className="relative flex flex-col items-center rounded-[28px] border border-[#E0E4EE] bg-[#FAFBFE] px-[34px] pb-[34px] pt-[30px] max-[680px]:p-[25px]"
      >
        <div className="relative z-[1] w-[142px] rounded-[18px] border border-[#DCE1EC] bg-white p-[13px] text-center max-[680px]:w-[118px]">
          <img
            src="/marketing/stems/artwork-18.webp"
            alt=""
            loading="lazy"
            className="block aspect-square w-full rounded-[11px] object-cover"
          />
          <span className="mt-[11px] block font-figtree text-[13px] font-extrabold leading-[1.2]">
            Finished recording
          </span>
        </div>
        <Connector />
        <StemResult
          label="Instrumental"
          icon={STEM_ICONS.instrumental}
          tone={ICON_TONES.teal}
          className="w-[min(250px,100%)]"
        />
        <Connector />
        <div className="relative z-[1] grid w-full grid-cols-2 gap-[11px]">
          {STEMS.map((stem) => (
            <StemResult key={stem.label} {...stem} />
          ))}
        </div>
      </div>
    </MarketingSection>
  );
}
